'use client';

import { IT_INT } from '@slims/shared/constants';
import type {
	CurveHealthMeterItem,
	CurveRegenScope,
	CurveSeriesItem,
	CurveZoomKey,
} from '@slims/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { api, apiFetch } from '@/lib/utils/api';
import { useAdminScopeStore } from '@/stores/admin-scope-store';

/**
 * Salute dei dati energetici dei punti di misura di un socio
 * (`GET /api/meters/health`).
 *
 * Non si aggiorna da sola e non è in realtime: la finestra è quella della
 * garanzia — `oggi−365 → oggi+7` — e a scriverla è la catena notturna, non
 * qualcosa che cambi mentre la scheda è aperta. Un canale realtime qui
 * mostrerebbe lo stesso numero per ventiquattr'ore pagando una sottoscrizione.
 *
 * A cambiarla mentre la scheda è aperta sono invece i due pulsanti di
 * rigenerazione, e per questo `regenerate` rilegge da sé quando ha finito: dopo
 * di loro i buchi e l'orizzonte sono diversi, ed è proprio quel «diversi» che
 * chi ha premuto sta aspettando di vedere.
 */
export interface CurveHealthResponse {
	window: { from: string; today: string; to: string };
	pastDays: number;
	futureDays: number;
	meters: CurveHealthMeterItem[];
}

/** Il consuntivo di una rigenerazione, come lo restituisce `regenerateCurves`. */
export interface CurveRegenResponse {
	scope: CurveRegenScope;
	window: { from: string; to: string };
	meters: number;
	repaired: number;
	withoutProfile: string[];
	failed: number;
	rows: number;
}

const EMPTY_WINDOW = { from: '', today: '', to: '' };
const EMPTY_METERS: CurveHealthMeterItem[] = [];
const EMPTY_SERIES: CurveSeriesItem[] = [];

const SCOPE_LABEL: Record<CurveRegenScope, string> = {
	storico: 'Dati storici',
	previsione: 'Previsione',
};

/**
 * ADR-006: backed by React Query. Copertura e serie sono due query distinte — la
 * prima è una riga per punto di misura e arriva subito, la seconda qualche
 * centinaio di punti per livello e si rilegge a ogni cambio di zoom.
 */
export function useCurveHealth(memberId: string | undefined, enabled = true) {
	const orgId = useAdminScopeStore((s) => s.configId);
	const active = !!memberId && !!orgId && enabled;

	const [regenerating, setRegenerating] = useState<CurveRegenScope | null>(null);
	const [note, setNote] = useState<string | null>(null);
	const [noteFailed, setNoteFailed] = useState(false);
	const [zoom, setZoom] = useState<CurveZoomKey>('anno');

	const healthQuery = useQuery({
		queryKey: ['curve-health', orgId, memberId],
		enabled: active,
		queryFn: () => {
			const params = new URLSearchParams({ memberId: memberId as string, orgId: orgId as string });
			return api.get<CurveHealthResponse>(`/api/meters/health?${params.toString()}`);
		},
	});

	const seriesQuery = useQuery({
		queryKey: ['curve-health-series', orgId, memberId, zoom],
		enabled: active,
		// Il grafico è un di più: un errore qui non alza un secondo messaggio, la
		// serie resta vuota e i numeri sopra restano leggibili.
		retry: false,
		queryFn: async () => {
			const params = new URLSearchParams({
				memberId: memberId as string,
				orgId: orgId as string,
				zoom,
			});
			const res = await api.get<{ meters: CurveSeriesItem[] }>(
				`/api/meters/health/series?${params.toString()}`,
			);
			return res.meters ?? [];
		},
	});

	const refetch = useCallback(async () => {
		await healthQuery.refetch();
	}, [healthQuery]);

	/**
	 * Rigenera uno dei due pezzi della garanzia e rilegge la scheda: mostra buchi
	 * e orizzonte, cioè ciò che la rigenerazione ha appena cambiato.
	 */
	const regenerate = useCallback(
		async (scope: CurveRegenScope) => {
			if (!memberId || !orgId) return;
			setRegenerating(scope);
			setNote(null);
			setNoteFailed(false);
			try {
				const res = await apiFetch(
					`/api/meters/health/regenerate?orgId=${encodeURIComponent(orgId)}`,
					{
						method: 'POST',
						headers: { 'Content-Type': 'application/json' },
						body: JSON.stringify({ memberId, scope }),
					},
				);
				const body = (await res.json()) as CurveRegenResponse & { error?: string };
				if (!res.ok) throw new Error(body.error ?? 'Rigenerazione fallita');
				const failed = body.failed > 0 || body.withoutProfile.length > 0;
				setNoteFailed(failed);
				setNote(
					`${SCOPE_LABEL[scope]}: ${body.rows.toLocaleString('it-IT', IT_INT)} righe su ` +
						`${body.meters} punti di misura, dal ${body.window.from} al ${body.window.to} (escluso)` +
						(body.failed > 0 ? ` · ${body.failed} punti falliti` : '') +
						(body.withoutProfile.length > 0
							? ` · ${body.withoutProfile.length} senza profilo GSE`
							: ''),
				);
				await Promise.all([healthQuery.refetch(), seriesQuery.refetch()]);
			} catch (e) {
				setNoteFailed(true);
				setNote(e instanceof Error ? e.message : 'Rigenerazione fallita');
			} finally {
				setRegenerating(null);
			}
		},
		[memberId, orgId, healthQuery, seriesQuery],
	);

	return {
		window: healthQuery.data?.window ?? EMPTY_WINDOW,
		pastDays: healthQuery.data?.pastDays ?? 0,
		futureDays: healthQuery.data?.futureDays ?? 0,
		meters: healthQuery.data?.meters ?? EMPTY_METERS,
		isLoading: active ? healthQuery.isLoading : false,
		error: healthQuery.error
			? healthQuery.error instanceof Error
				? healthQuery.error.message
				: 'Errore nella verifica dei dati energetici'
			: null,
		refetch,
		regenerate,
		regenerating,
		note,
		noteFailed,
		series: seriesQuery.data ?? EMPTY_SERIES,
		seriesLoading: active ? seriesQuery.isLoading : false,
		zoom,
		setZoom,
	};
}
